import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FlaskConical, Dna, History, Beaker, ChevronDown,
  Trash2, Download, Loader2, Play, RotateCcw, X,
  ChevronRight, Menu, Info, Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ========== Components ==========

const SequenceInput = ({ value, onChange, label, placeholder, chainType }) => {
  const charCount = value.replace(/\s/g, '').length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${chainType === 'H' ? 'bg-cyan-500' : 'bg-teal-500'}`}>
            {chainType}
          </span>
          {label}
        </label>
        <span className="text-xs text-muted-foreground font-mono">{charCount} residues</span>
      </div>
      <div className="input-wrapper">
        <textarea
          data-testid={`sequence-input-${chainType.toLowerCase()}`}
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          placeholder={placeholder}
          className="w-full h-32 p-4 font-mono text-sm tracking-wide uppercase bg-muted/30 
                     rounded-xl border-0 resize-none focus:outline-none focus:ring-0"
          spellCheck={false}
        />
      </div>
    </div>
  );
};

const ParatopeViewer = ({ result }) => {
  if (!result || !result.residues || result.residues.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No residues to display
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${result.chain_type === 'H' ? 'bg-cyan-500' : 'bg-teal-500'}`}>
            {result.chain_type}
          </span>
          <span className="text-sm font-medium">{result.chain_type === 'H' ? 'Heavy' : 'Light'} Chain</span>
          <span className="text-xs text-muted-foreground ml-2">({result.total_residues} residues)</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-cyan-400"></div>
            <span className="text-muted-foreground">Paratope ({result.paratope_count})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-slate-200"></div>
            <span className="text-muted-foreground">Non-paratope</span>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
          <ChevronRight className="w-3 h-3" />
          <span>Scroll horizontally to view all residues</span>
        </div>
        <div className="sequence-track bg-slate-50/50 rounded-xl border overflow-x-auto custom-scrollbar">
          {result.residues.map((res, idx) => (
            <div
              key={idx}
              className={`sequence-residue flex-shrink-0 ${res.is_paratope ? 'paratope' : 'non-paratope'}`}
              title={`${res.amino_acid}${res.position}: ${(res.score * 100).toFixed(1)}%`}
            >
              <div className="flex flex-col items-center">
                <span className="text-[10px] opacity-60">{res.position}</span>
                <span>{res.amino_acid}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ResultsTable = ({ result }) => {
  if (!result || !result.residues) return null;

  const getConfidenceClass = (score) => {
    if (score >= 0.7) return 'confidence-high';
    if (score >= 0.4) return 'confidence-medium';
    return 'confidence-low';
  };

  const getConfidenceLabel = (score) => {
    if (score >= 0.7) return 'High';
    if (score >= 0.4) return 'Medium';
    return 'Low';
  };

  return (
    <div className="rounded-xl border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="results-header bg-muted/30">
            <TableHead className="w-20">Position</TableHead>
            <TableHead className="w-24">Residue</TableHead>
            <TableHead className="w-28">Score</TableHead>
            <TableHead className="w-28">Confidence</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {result.residues.map((res, idx) => (
            <TableRow key={idx} className={res.is_paratope ? 'bg-cyan-50/50' : ''}>
              <TableCell className="font-mono text-sm">{res.position}</TableCell>
              <TableCell className="font-mono font-semibold">{res.amino_acid}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${res.is_paratope ? 'bg-cyan-500' : 'bg-slate-400'}`}
                      style={{ width: `${res.score * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">
                    {(res.score * 100).toFixed(1)}%
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <span className={`confidence-badge ${getConfidenceClass(res.score)}`}>
                  {getConfidenceLabel(res.score)}
                </span>
              </TableCell>
              <TableCell>
                {res.is_paratope ? (
                  <span className="flex items-center gap-1 text-cyan-600 font-medium">
                    <Sparkles className="w-4 h-4" /> Paratope
                  </span>
                ) : (
                  <span className="text-muted-foreground">Non-paratope</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const HistoryPanel = ({ history, onSelect, onDelete, onClear, selectedId }) => {
  if (history.length === 0) {
    return (
      <div className="empty-state">
        <History className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p className="font-medium">No predictions yet</p>
        <p className="text-sm mt-1">Your prediction history will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Recent Predictions
        </h3>
        <Button variant="ghost" size="sm" onClick={onClear} className="text-destructive hover:text-destructive">
          <Trash2 className="w-4 h-4 mr-1" /> Clear All
        </Button>
      </div>
      <ScrollArea className="h-[400px]">
        <div className="space-y-2 pr-4">
          {history.map((entry) => (
            <div
              key={entry.id}
              className={`history-item ${selectedId === entry.id ? 'active' : ''}`}
              onClick={() => onSelect(entry)}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    {new Date(entry.timestamp).toLocaleString()}
                  </p>
                  <div className="flex items-center gap-2">
                    {entry.sequence_h && (
                      <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full">
                        H: {entry.paratope_count_h} paratopes
                      </span>
                    )}
                    {entry.sequence_l && (
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                        L: {entry.paratope_count_l} paratopes
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(entry.id);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

const SampleSelector = ({ samples, onSelect }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="sample-selector">
          <Beaker className="w-4 h-4" />
          Load Sample
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        {samples.map((sample, idx) => (
          <DropdownMenuItem
            key={idx}
            onClick={() => onSelect(sample)}
            className="flex flex-col items-start gap-1 py-3 cursor-pointer"
          >
            <span className="font-medium">{sample.name}</span>
            <span className="text-xs text-muted-foreground">{sample.description}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ========== Main App ==========

const ParaDeepApp = () => {
  const [sequenceH, setSequenceH] = useState("");
  const [sequenceL, setSequenceL] = useState("");
  const [resultH, setResultH] = useState(null);
  const [resultL, setResultL] = useState(null);
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [activeTab, setActiveTab] = useState("visualization");
  const [showHistory, setShowHistory] = useState(false);
  const [totalVisits, setTotalVisits] = useState(0);

  // Load samples and history on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [samplesRes, historyRes, visitRes] = await Promise.all([
          axios.get(`${API}/samples`),
          axios.get(`${API}/history`),
          axios.post(`${API}/visit?path=${window.location.pathname}`)
        ]);
        setSamples(samplesRes.data);
        setHistory(historyRes.data);
        if (visitRes.data?.success) {
          setTotalVisits(visitRes.data.total_visits);
        }
      } catch (e) {
        console.error("Failed to load data:", e);
      }
    };
    loadData();
  }, []);

  const predict = useCallback(async () => {
    if (!sequenceH.trim() && !sequenceL.trim()) {
      toast.error("Please enter at least one sequence");
      return;
    }

    setLoading(true);
    setResultH(null);
    setResultL(null);

    try {
      const requests = [];

      if (sequenceH.trim()) {
        requests.push(
          axios.post(`${API}/predict`, {
            sequence: sequenceH.replace(/\s/g, ''),
            chain_type: "H"
          })
        );
      }

      if (sequenceL.trim()) {
        requests.push(
          axios.post(`${API}/predict`, {
            sequence: sequenceL.replace(/\s/g, ''),
            chain_type: "L"
          })
        );
      }

      const results = await Promise.all(requests);

      let newResultH = null;
      let newResultL = null;

      results.forEach(res => {
        if (res.data.chain_type === 'H') {
          newResultH = res.data;
          setResultH(res.data);
        } else {
          newResultL = res.data;
          setResultL(res.data);
        }
      });

      // Simple UUID generator for compatibility (http context)
      const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      };

      // Save to history
      const historyEntry = {
        id: generateUUID(),
        timestamp: new Date().toISOString(),
        sequence_h: sequenceH.trim() || null,
        sequence_l: sequenceL.trim() || null,
        result_h: newResultH,
        result_l: newResultL,
        paratope_count_h: newResultH?.paratope_count || 0,
        paratope_count_l: newResultL?.paratope_count || 0
      };

      try {
        await axios.post(`${API}/history`, historyEntry);
        setHistory(prev => [historyEntry, ...prev]);
        setSelectedHistoryId(historyEntry.id);
      } catch (historyError) {
        console.error("History saving failed:", historyError);
        toast.warning("Prediction complete, but failed to save history.");
      }

      toast.success("Prediction complete!");
    } catch (e) {
      console.error("Prediction failed:", e);
      toast.error("Prediction failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [sequenceH, sequenceL]);

  const handleSampleSelect = (sample) => {
    setSequenceH(sample.heavy_chain);
    setSequenceL(sample.light_chain);
    toast.success(`Loaded ${sample.name}`);
  };

  const handleHistorySelect = (entry) => {
    setSelectedHistoryId(entry.id);
    if (entry.sequence_h) setSequenceH(entry.sequence_h);
    if (entry.sequence_l) setSequenceL(entry.sequence_l);
    if (entry.result_h) setResultH(entry.result_h);
    if (entry.result_l) setResultL(entry.result_l);
  };

  const handleHistoryDelete = async (id) => {
    try {
      await axios.delete(`${API}/history/${id}`);
      setHistory(prev => prev.filter(h => h.id !== id));
      if (selectedHistoryId === id) {
        setSelectedHistoryId(null);
      }
      toast.success("Entry deleted");
    } catch (e) {
      toast.error("Failed to delete entry");
    }
  };

  const handleClearHistory = async () => {
    try {
      await axios.delete(`${API}/history`);
      setHistory([]);
      setSelectedHistoryId(null);
      toast.success("History cleared");
    } catch (e) {
      toast.error("Failed to clear history");
    }
  };

  const clearAll = () => {
    setSequenceH("");
    setSequenceL("");
    setResultH(null);
    setResultL(null);
    setSelectedHistoryId(null);
  };

  const downloadResults = () => {
    const results = [];

    if (resultH) {
      resultH.residues.forEach(r => {
        results.push({
          chain: 'H',
          position: r.position,
          amino_acid: r.amino_acid,
          score: r.score.toFixed(4),
          is_paratope: r.is_paratope
        });
      });
    }

    if (resultL) {
      resultL.residues.forEach(r => {
        results.push({
          chain: 'L',
          position: r.position,
          amino_acid: r.amino_acid,
          score: r.score.toFixed(4),
          is_paratope: r.is_paratope
        });
      });
    }

    if (results.length === 0) {
      toast.error("No results to download");
      return;
    }

    const headers = ['chain', 'position', 'amino_acid', 'score', 'is_paratope'];
    const csv = [
      headers.join(','),
      ...results.map(r => headers.map(h => r[h]).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paradeep_results_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Results downloaded!");
  };

  const hasResults = resultH || resultL;
  const totalParatopes = (resultH?.paratope_count || 0) + (resultL?.paratope_count || 0);
  const totalResidues = (resultH?.total_residues || 0) + (resultL?.total_residues || 0);

  return (
    <div className="min-h-screen bg-background" data-testid="paradeep-app">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_fa71fc86-184d-4371-b50a-ee1feeaf4a14/artifacts/po6peszh_download.png"
                alt="ParaDeep Logo"
                className="w-10 h-10 rounded-xl shadow-lg object-cover"
              />
              <div>
                <h1 className="text-xl font-bold tracking-tight">ParaDeep</h1>
                <p className="text-xs text-muted-foreground">Paratope Prediction</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Info className="w-5 h-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>About ParaDeep</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 text-sm">
                    <p>
                      ParaDeep uses BiLSTM-CNN deep learning models to predict paratope residues
                      in antibody sequences. Paratopes are the regions of an antibody that bind
                      to antigens.
                    </p>
                    <div className="space-y-2">
                      <h4 className="font-semibold">How to use:</h4>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Enter your antibody sequence(s) in the input fields</li>
                        <li>Use H-chain for heavy chain, L-chain for light chain</li>
                        <li>Click "Predict Paratopes" to run the analysis</li>
                        <li>View results in the visualization or table view</li>
                      </ol>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold">Model:</h4>
                      <p className="text-muted-foreground">
                        Chain-aware BiLSTM-CNN trained on known antibody-antigen structures.
                        Separate models for heavy and light chains.
                      </p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowHistory(!showHistory)}
                className={showHistory ? 'bg-muted' : ''}
              >
                <History className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Main Panel */}
          <div className={`space-y-6 ${showHistory ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
            {/* Input Section */}
            <div className="glass-panel rounded-2xl p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-primary" />
                  Sequence Input
                </h2>
                <div className="flex items-center gap-2">
                  <SampleSelector samples={samples} onSelect={handleSampleSelect} />
                  <Button variant="ghost" size="sm" onClick={clearAll}>
                    <RotateCcw className="w-4 h-4 mr-1" /> Reset
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <SequenceInput
                  value={sequenceH}
                  onChange={setSequenceH}
                  label="Heavy Chain Sequence"
                  placeholder="Enter H-chain amino acid sequence (e.g., EVQLVESGGGLVQ...)"
                  chainType="H"
                />
                <SequenceInput
                  value={sequenceL}
                  onChange={setSequenceL}
                  label="Light Chain Sequence"
                  placeholder="Enter L-chain amino acid sequence (e.g., DIQMTQSPS...)"
                  chainType="L"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-sm text-muted-foreground">
                  Enter antibody sequences to predict paratope residues
                </p>
                <Button
                  data-testid="predict-btn"
                  onClick={predict}
                  disabled={loading || (!sequenceH.trim() && !sequenceL.trim())}
                  className="btn-primary"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Predicting...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Predict Paratopes
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Results Section */}
            {hasResults && (
              <div className="space-y-6 animate-fadeInUp" data-testid="result-view">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="stat-card">
                    <div className="stat-value gradient-text">{totalParatopes}</div>
                    <div className="stat-label">Paratope Residues</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{totalResidues}</div>
                    <div className="stat-label">Total Residues</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">
                      {totalResidues > 0 ? ((totalParatopes / totalResidues) * 100).toFixed(1) : 0}%
                    </div>
                    <div className="stat-label">Paratope Ratio</div>
                  </div>
                  <div className="stat-card flex items-center justify-center">
                    <Button variant="outline" onClick={downloadResults} className="gap-2">
                      <Download className="w-4 h-4" />
                      Download CSV
                    </Button>
                  </div>
                </div>

                {/* Tabs for visualization/table */}
                <div className="glass-panel rounded-2xl p-6">
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="flex items-center justify-between mb-6">
                      <TabsList>
                        <TabsTrigger value="visualization" className="gap-2">
                          <Dna className="w-4 h-4" /> Visualization
                        </TabsTrigger>
                        <TabsTrigger value="table" className="gap-2">
                          <Table className="w-4 h-4" /> Table View
                        </TabsTrigger>
                      </TabsList>
                    </div>

                    <TabsContent value="visualization" className="space-y-6 tab-content">
                      {resultH && <ParatopeViewer result={resultH} />}
                      {resultL && <ParatopeViewer result={resultL} />}
                    </TabsContent>

                    <TabsContent value="table" className="tab-content">
                      <div className="space-y-6">
                        {resultH && (
                          <div>
                            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center text-xs text-white font-bold">H</span>
                              Heavy Chain Results
                            </h3>
                            <ScrollArea className="h-[300px]">
                              <ResultsTable result={resultH} />
                            </ScrollArea>
                          </div>
                        )}
                        {resultL && (
                          <div>
                            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center text-xs text-white font-bold">L</span>
                              Light Chain Results
                            </h3>
                            <ScrollArea className="h-[300px]">
                              <ResultsTable result={resultL} />
                            </ScrollArea>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            )}

            {/* Empty state */}
            {!hasResults && !loading && (
              <div className="glass-panel rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-teal-500/10 flex items-center justify-center">
                  <Dna className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Ready to Predict</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Enter your antibody sequences above or load a sample to get started.
                  ParaDeep will identify potential binding sites (paratopes) in your sequences.
                </p>
              </div>
            )}
          </div>

          {/* History Panel */}
          {showHistory && (
            <div className="lg:col-span-1">
              <div className="glass-panel rounded-2xl p-4 sticky top-24">
                <HistoryPanel
                  history={history}
                  onSelect={handleHistorySelect}
                  onDelete={handleHistoryDelete}
                  onClear={handleClearHistory}
                  selectedId={selectedHistoryId}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-end gap-1">
            <p className="text-sm text-muted-foreground">
              Chain-aware deep learning for antibody analysis
            </p>
            {totalVisits > 0 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground/60">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                <span>{totalVisits.toLocaleString()} total visits</span>
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

const VisitorStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (e) {
        console.error("Failed to fetch stats", e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="p-8 text-center">Loading stats...</div>;
  if (!stats) return <div className="p-8 text-center">Stats not available</div>;

  return (
    <div className="max-w-4xl mx-auto p-8">
      <h1 className="text-2xl font-bold mb-6">Visitor Statistics</h1>
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <p className="text-4xl font-bold text-indigo-600">{stats.total_visits.toLocaleString()}</p>
        <p className="text-gray-500">Total Visits</p>
      </div>

      <h2 className="text-xl font-semibold mb-4">Recent Log</h2>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Agent</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {stats.recent_visitors.map((v, i) => (
              <tr key={i}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(v.timestamp).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{v.ip}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{v.path}</td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={v.user_agent}>{v.user_agent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<ParaDeepApp />} />
        <Route path="/secret-dashboard" element={<VisitorStats />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
